import { type PropsWithChildren, forwardRef } from 'react'
import clsx from 'clsx'

import ChevronDown from '@sylveraio/untitled-ui-icons-react/build/esm/ChevronDown'

import { Typography } from '../Typography'

import type { DropdownProps } from './types'

export const Dropdown = forwardRef<
  HTMLDivElement,
  PropsWithChildren<DropdownProps>
>(
  (
    {
      children,
      isExpanded = false,
      menuClassName,
      onClick,
      placeholder,
      size = 'sm',
      value,
      isNeutral = false,
      header,
      ...rest
    },
    ref,
  ) => {
    return (
      <div className="relative w-full">
        <button
          {...rest}
          onClick={onClick}
          className={clsx(
            'flex w-full items-center justify-between space-x-2 rounded-lg border px-3 outline-0 ring-content-action-subtle ring-offset-2 focus-visible:border-content-action-default focus-visible:ring-4',
            {
              'h-12': size === 'lg',
              'h-9': size === 'sm',
              'bg-content-action-default border-content-action-dark':
                isExpanded && !isNeutral,
              'bg-high border-default hover:border-default-hover hover:bg-high-hover':
                (!isExpanded && !value) || isNeutral,
              'bg-high border-2 border-content-action-default hover:bg-high-hover':
                !isExpanded && value && !isNeutral,
            },
          )}
        >
          <Typography
            size="sm"
            weight="medium"
            className={clsx('truncate', {
              'text-default-on-dark': isExpanded,
              'text-subtle': (!isExpanded && !value) || isNeutral,
              '!text-content-action-default':
                !isExpanded && value && !isNeutral,
            })}
          >
            {value || placeholder}
          </Typography>
          <ChevronDown
            className={clsx('w-5 h-5', {
              'text-default-on-dark transform rotate-180': isExpanded,
              'text-content-action-default': !isExpanded && value && !isNeutral,
            })}
          />
        </button>

        {isExpanded && (
          <div
            ref={ref}
            className={clsx(
              'absolute z-50 mt-1 min-w-full rounded-lg border border-default bg-high py-1.5 shadow-md overflow-y-auto',
              menuClassName,
            )}
          >
            {header && (
              <Typography
                size="sm"
                weight="medium"
                className="px-4 py-2 text-default"
                aria-hidden="true"
              >
                {header}
              </Typography>
            )}
            {children}
          </div>
        )}
      </div>
    )
  },
)
